import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Card } from 'src/components/ui/card';
import { ScrollArea } from 'src/components/ui/scroll-area';
import {
  UploadIcon,
  CheckCircleIcon,
  XCircleIcon,
  SpinnerIcon,
  FileIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import { rpcClient } from 'src/api/rpc/client';
import { useTestStep } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { useDocumentPreview } from 'src/api/rpc/hooks/core/useDocuments';
import type { StepNodeData } from './config-renderers/types';

interface StepTestSectionProps {
  stepType: string;
  config: Record<string, unknown>;
  inputSockets: Array<{
    id: string;
    name: string;
    type: string;
    required?: boolean;
    description?: string;
  }>;
}

interface TestResult {
  success: boolean;
  output?: Record<string, unknown>;
  emittedEvents?: Array<{ eventType: string; data: unknown }>;
  error?: string;
  durationMs?: number;
}

function getDocumentSockets(
  sockets: StepTestSectionProps['inputSockets'],
): StepTestSectionProps['inputSockets'] {
  return sockets.filter(
    (s) =>
      s.type === 'entity_reference:CORE_DOCUMENT' ||
      s.type === 'entity_reference',
  );
}

export function hasDocumentInputSockets(
  stepTypeData?: StepNodeData['_stepTypeData'],
): boolean {
  if (!stepTypeData?.inputSockets) return false;
  return getDocumentSockets(stepTypeData.inputSockets).length > 0;
}

export function StepTestSection({
  stepType,
  config,
  inputSockets,
}: StepTestSectionProps) {
  const gt = useGT();
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'running' | 'done'>(
    'idle',
  );
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const testStepMutation = useTestStep();
  const isRunningRef = useRef(false);

  const documentSockets = getDocumentSockets(inputSockets);

  const runTest = useCallback(
    async (file: File) => {
      if (documentSockets.length === 0) return;
      if (isRunningRef.current) return;
      isRunningRef.current = true;

      setSelectedFile(file);
      setPhase('uploading');
      setTestResult(null);

      try {
        const entityId = uuidv4();
        const { documentId, uploadUrl } =
          await rpcClient.core.documents.getUploadUrl({
            entityType: 'workflow_step_test',
            entityId,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
          });

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'x-ms-blob-type': 'BlockBlob',
          },
        });
        if (!uploadResponse.ok) {
          throw new Error(
            `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          );
        }

        await rpcClient.core.documents.confirmUpload({
          documentId,
          entityType: 'workflow_step_test',
          entityId,
          fileSizeBytes: file.size,
        });

        setPhase('running');

        const input: Record<string, unknown> = {};
        for (const socket of inputSockets) {
          if (
            socket.type === 'entity_reference:CORE_DOCUMENT' ||
            socket.type === 'entity_reference'
          ) {
            input[socket.id] = {
              entityType: 'CORE_DOCUMENT',
              entityId: documentId,
            };
          }
        }

        const result = await testStepMutation.mutateAsync({
          stepType,
          input,
          config,
          timeoutMs: 120_000,
        });

        setTestResult(result);
        setPhase('done');
      } catch (error) {
        setTestResult({
          success: false,
          error: error instanceof Error ? error.message : String(error),
        });
        setPhase('done');
      } finally {
        isRunningRef.current = false;
      }
    },
    [documentSockets, inputSockets, stepType, config, testStepMutation],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        runTest(files[0]);
      }
    },
    [runTest],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleBrowseClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        runTest(target.files[0]);
      }
    };
    input.click();
  }, [runTest]);

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setTestResult(null);
    setPhase('idle');
  }, []);

  if (documentSockets.length === 0) {
    return (
      <p className="p-3 text-xs text-muted-foreground">
        <T context="step test section no document sockets">
          This step does not accept document inputs.
        </T>
      </p>
    );
  }

  const isProcessing = phase === 'uploading' || phase === 'running';

  return (
    <div className="space-y-3 p-3">
      <div className="space-y-1">
        <p className="text-xs font-medium">
          <T context="step test section title">
            <T context="workflows">Test with Document</T>
          </T>
        </p>
        <p className="text-[11px] text-muted-foreground">
          <T context="step test section description">
            Upload a document to run this step in isolation with the current
            config.
          </T>
        </p>
      </div>

      {/* Dropzone */}
      <Card
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed p-4 transition-colors cursor-pointer ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
        onClick={!isProcessing ? handleBrowseClick : undefined}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <UploadIcon className="h-6 w-6 text-muted-foreground" />
          {selectedFile ? (
            <div className="space-y-1">
              <p className="text-xs font-medium truncate max-w-[200px]">
                {selectedFile.name}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              <T context="step test dropzone hint">
                Drop a file here or click to browse
              </T>
            </p>
          )}
        </div>
      </Card>

      {/* Status / actions */}
      {isProcessing && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <SpinnerIcon className="h-3.5 w-3.5 animate-spin" />
          {phase === 'uploading'
            ? gt('Uploading...', {
                context: 'step test section uploading document status',
              })
            : gt('Running step...', {
                context: 'step test section running step status',
              })}
        </div>
      )}
      {(selectedFile || testResult) && !isProcessing && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleClear}
          className="h-7 text-xs"
        >
          <T context="step test section clear results button">Clear</T>
        </Button>
      )}

      {/* Results */}
      {testResult && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {testResult.success ? (
              <CheckCircleIcon className="h-4 w-4 text-green-600" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-destructive" />
            )}
            <span className="text-xs font-medium">
              {testResult.success
                ? gt('Success', { context: 'step test result success status' })
                : gt('Failed', { context: 'step test result failure status' })}
            </span>
            {testResult.durationMs != null && (
              <Badge variant="outline" className="text-[10px]">
                {(testResult.durationMs / 1000).toFixed(1)}
                {gt('s', {
                  context: 'seconds unit abbreviation after a duration number',
                })}
              </Badge>
            )}
          </div>

          {testResult.error && (
            <div className="rounded-md bg-destructive/10 p-2">
              <p className="text-xs text-destructive break-all">
                {testResult.error}
              </p>
            </div>
          )}

          {testResult.output && (
            <TestOutputDisplay output={testResult.output} />
          )}

          {testResult.emittedEvents && testResult.emittedEvents.length > 0 && (
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">
                <T context="step test emitted events count label">
                  Emitted Events (<Var>{testResult.emittedEvents.length}</Var>)
                </T>
              </p>
              <ScrollArea className="max-h-32">
                <pre className="rounded-md bg-muted p-2 text-[11px] whitespace-pre-wrap break-all">
                  {JSON.stringify(testResult.emittedEvents, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface EntityRef {
  entityType: string;
  entityId: string;
}

function isEntityRef(val: unknown): val is EntityRef {
  return (
    typeof val === 'object' &&
    val !== null &&
    'entityType' in val &&
    'entityId' in val &&
    typeof (val as EntityRef).entityType === 'string' &&
    typeof (val as EntityRef).entityId === 'string'
  );
}

function extractDocumentRefs(output: Record<string, unknown>): EntityRef[] {
  const refs: EntityRef[] = [];
  for (const value of Object.values(output)) {
    if (isEntityRef(value) && value.entityType === 'CORE_DOCUMENT') {
      refs.push(value);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (isEntityRef(item) && item.entityType === 'CORE_DOCUMENT') {
          refs.push(item);
        }
      }
    }
  }
  return refs;
}

function hasNonDocumentOutput(
  output: Record<string, unknown>,
  docRefs: EntityRef[],
): boolean {
  const docIds = new Set(docRefs.map((r) => r.entityId));
  for (const value of Object.values(output)) {
    if (isEntityRef(value) && docIds.has(value.entityId)) continue;
    if (Array.isArray(value)) {
      const nonDocItems = value.filter(
        (item) => !(isEntityRef(item) && docIds.has(item.entityId)),
      );
      if (nonDocItems.length > 0) return true;
      continue;
    }
    return true;
  }
  return false;
}

function DocumentCard({ docRef, index }: { docRef: EntityRef; index: number }) {
  const gt = useGT();
  const { data, isLoading } = useDocumentPreview(docRef.entityId);

  const handleClick = () => {
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!data?.signedUrl}
      className="flex w-full items-center gap-2 rounded-md border p-2 text-left transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-60"
    >
      <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">
            <T context="workflows">Loading...</T>
          </p>
        ) : data ? (
          <>
            <p className="truncate text-xs font-medium">{data.fileName}</p>
            {data.mimeType && (
              <p className="text-[10px] text-muted-foreground">
                {data.mimeType}
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            {gt('Document {n}', {
              context:
                'step test output document card label when name is unavailable',
              n: String(index + 1),
            })}
          </p>
        )}
      </div>
      <ArrowSquareOutIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

function TestOutputDisplay({ output }: { output: Record<string, unknown> }) {
  const docRefs = extractDocumentRefs(output);
  const showRawOutput = hasNonDocumentOutput(output, docRefs);

  return (
    <div className="space-y-2">
      {docRefs.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground">
            <T context="step test documents count label">
              Documents (<Var>{docRefs.length}</Var>)
            </T>
          </p>
          <div className="space-y-1.5">
            {docRefs.map((ref, i) => (
              <DocumentCard key={ref.entityId} docRef={ref} index={i} />
            ))}
          </div>
        </div>
      )}
      {showRawOutput && (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground">
            <T context="step test output section label">Output</T>
          </p>
          <ScrollArea className="max-h-48">
            <pre className="rounded-md bg-muted p-2 text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(output, null, 2)}
            </pre>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
